import type { ImageItem, RootScope } from '../types';
import type { SectionSvgUploadResource } from './section.svg.upload.resource';
import type { ImageUploadResponse } from './types';

import angular from 'angular';

import previewTemplate from '../section.svg.upload/preview.dialog.html';
import { notify } from '../ui';

interface NgFile extends File {
  $error?: string;
  $errorParam?: string;
}

export class sectionSvgUploadCtrl {
  static $inject = [
    '$scope',
    'SectionSvgUpload',
    'Upload',
    '$rootScope',
    '$translate',
    '$mdDialog',
  ];

  declare sectionId: string;
  declare progress: number;

  /**
   * image for navigation from client
   */
  declare sourceImages: ImageItem[];

  /**
   * rescaled and production ready image for navigation from our operators
   */
  declare previewImages: ImageItem[];
  declare previewSvgUrl: string | null;
  declare previewImageUrl: string | null;
  declare finalSvgUrl: string | null;
  declare finalImageUrl: string | null;

  constructor(
    private $scope: angular.IScope,
    public SectionSvgUpload: SectionSvgUploadResource,
    private Upload: any,
    private $rootScope: RootScope,
    private $translate: angular.translate.ITranslateService,
    private $mdDialog: angular.material.IDialogService,
  ) {
    this.sourceImages = this.sourceImages || [];
    this.previewImages = this.previewImages || [];

    $scope.$watch(
      () => this.previewImages.length,
      () => {
        if (this.previewImages.length) {
          this.previewImageUrl = this.previewImages[
            this.previewImages.length - 1
          ].url.replace(/^http:/, 'https:');
        }
      },
    );

    SectionSvgUpload.fetchImages({
      sectionId: this.sectionId,
      finalOnly: false,
    }).then((images) => {
      $scope.$apply(() => {
        this.previewSvgUrl = images.previewSvg;
        this.finalSvgUrl = images.finalSvg;
        this.finalImageUrl = images.finalImage;
      });
    });
  }

  refreshImages = async () => {
    const images = await this.SectionSvgUpload.fetchImages({
      sectionId: this.sectionId,
    });

    this.$scope.$apply(() => {
      this.finalSvgUrl = images.finalSvg;
      this.finalImageUrl = images.finalImage;
    });
  };

  showNaviPreview = (
    event: MouseEvent,
    imageUrl: string | null,
    svgUrl: string | null,
  ) => {
    if (!imageUrl || !svgUrl) {
      return;
    }

    const vm = this;

    this.$mdDialog.show({
      targetEvent: event,
      multiple: true,
      controller: [
        '$mdDialog',
        function DialogController(this: { [key: string]: any }, $mdDialog) {
          this.close = () => {
            $mdDialog.hide();
          };

          this.publish = async () => {
            try {
              await vm.SectionSvgUpload.approveImages(
                {
                  id: vm.sectionId,
                },
                {},
              ).$promise;

              vm.finalImageUrl = vm.previewImageUrl;
              vm.finalSvgUrl = vm.previewSvgUrl;
              vm.previewSvgUrl = null;
              vm.sourceImages = [];
              vm.previewImages = [];

              vm.$scope.$apply();

              notify.success(vm.$translate.instant('navi.publish.success'));

              this.close();
            } catch (error) {
              notify.error(vm.$translate.instant('navi.publish.error'), error, {
                message: 'Error publishing navigation',
              });
            }
          };
        },
      ],
      bindToController: true,
      controllerAs: 'vm',
      locals: {
        sectionId: this.sectionId,
        svgUrl,
        imageUrl,
        published: !!this.finalSvgUrl && svgUrl === this.finalSvgUrl,
      },
      template: previewTemplate,
    });
  };

  uploadFiles = async (
    files: NgFile[],
    type: 'naviSectionPreviewSvg',
  ): Promise<void> => {
    const [file] = files;

    if (!file) {
      return;
    }

    try {
      if (file.$error && file.$error === 'maxSize') {
        throw new Error(
          `Your file exceeds ${file.$errorParam}. Please, resize.`,
        );
      }

      const svgCode = await new Promise<string>((resolve, reject) => {
        // validate svg file
        const reader = new FileReader();
        reader.addEventListener('load', (event) => {
          if (!event.target || !event.target.result) {
            reject(new Error('Can not read file'));

            return;
          }

          resolve(String(event.target.result));
        });

        reader.readAsText(file);
      });

      const $svg = angular.element(svgCode).filter('svg');

      if (!$svg[0]) {
        throw new Error('Can not find svg root');
      }

      $svg.find('rect, polygon').each((index, el) => {
        // eslint-disable-next-line prefer-destructuring
        const text = angular.element(el).next('text')[0];

        if (!text) {
          throw new Error(`Expected <text> element after ${el.outerHTML}`);
        }
      });

      const response: { data: ImageUploadResponse } = await this.Upload.upload({
        method: 'POST',
        url: '/images/upload?act=submit',
        file,
        fields: {
          type,
          id: this.sectionId,
          token: this.$rootScope.token,
        },
      }).progress((evt) => {
        this.progress = Math.floor((100 * evt.loaded) / evt.total);
      });

      this.progress = 0;

      if (!response.data.status) {
        throw Object.assign(new Error('Error uploading image'), {
          response,
        });
      }

      this.previewSvgUrl = response.data.result.url.replace(/^http:/, 'https:');

      this.$scope.$apply();

      notify.success(this.$translate.instant('svg.upload.success'));
    } catch (error) {
      notify.error(
        `${this.$translate.instant('svg.upload.format.error')}${
          error.message || ''
        }`,
        error,
        {
          message: 'Error processing svg',
        },
      );
    }
  };
}
